import { createFileRoute } from '@tanstack/react-router';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import TablerIconBrandTailwind from '~icons/tabler/brand-tailwind';
import TablerIconChevronDown from '~icons/tabler/chevron-down';
import TablerIconMoodSmileFilled from '~icons/tabler/mood-smile-filled';
import TablerIconX from '~icons/tabler/x';

import {
  Accordion,
  AccordionDetails,
  AccordionItem,
  AccordionSummary,
  Alert,
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  type CommonProps,
  Container,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Grid,
  IconButton,
  Loading,
  Progress,
  Slider,
  Stack,
  Typography,
} from 'gooey';
import Tooltip from '#/ui/components/tooltip';

export const Route = createFileRoute('/_blank/style-guide')({
  component: GooeyDesignSystemPage,
});

function GooeyDesignSystemPage() {
  const [links, setLinks] = useState<string[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (links.length > 0) {
      return;
    }

    const id = setInterval(() => {
      const els = Array.from(document.querySelectorAll('.styleguide-section'));
      setLinks(els.map((el) => el.id) ?? []);
    }, 500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className='min-h-screen py-8'>
      <div className='h-full w-full flex flex-col items-center justify-center max-w-screen mx-auto'>
        <Container maxWidth='2xl'>
          <div className='mb-8 text-center'>
            <div className='d-avatar mb-4'>
              <div className='ring-primary-dark d-ring d-ring-offset-base-100 w-full p-4 bg-primary rounded-full'>
                <TablerIconBrandTailwind className='size-16 mx-auto mb-4 ' />
              </div>
            </div>
            <h1 className='text-5xl mb-2 font-bold'>Gooey Design System</h1>
            <p className='text-lg'>
              A design system built on Tailwind CSS and DaisyUI that mirrors the MUI components
            </p>
            <Stack
              direction='row'
              spacing={0.5}
              justifyContent='center'
              className='mt-4 min-h-12'
              divider='|'
            >
              {links.map((link) => (
                <a
                  key={link}
                  className='link link-primary'
                  href={`#${link}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(link);
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {link}
                </a>
              ))}
            </Stack>
          </div>
          <Grid container columns={12} spacing={4} className='mb-8'>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Loading'>
                <GooeyVariantSection
                  title='Colors'
                  direction='row'
                  variants={[
                    ...(['spinner', 'dots', 'pulse', 'ball', 'bars', 'infinity'] as const).map(
                      (variant) => ({
                        title: variant,
                        variant: <Loading key={variant} variant={variant} size='md' />,
                      })
                    ),
                  ]}
                />
                <GooeyVariantSection
                  title='Sizes'
                  direction='row'
                  variants={[
                    ...(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((size) => ({
                      title: size,
                      variant: (
                        <Loading className='min-h-24' key={size} size={size} variant='spinner' />
                      ),
                    })),
                  ]}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Slider' className='min-h-64'>
                <Slider />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Tooltip' className='min-h-64'>
                <GooeyVariantSection
                  title='Examples'
                  variants={[
                    {
                      title: 'basic',
                      variant: (
                        <Tooltip title='This is a tooltip' placement='top'>
                          <Button>Hover me</Button>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'bigger',
                      variant: (
                        <Tooltip title='This is a tooltip' placement='top'>
                          <div className='size-64 bg-accent-dark rounded items-center justify-center flex'>
                            Hover me
                          </div>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
                <GooeyVariantSection
                  title='Placement'
                  variants={(['top', 'bottom', 'left', 'right'] as const).map((placement) => ({
                    title: placement,
                    variant: (
                      <Tooltip title='This is a tooltip' placement={placement}>
                        <Button>Hover me</Button>
                      </Tooltip>
                    ),
                  }))}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Color Palette'>
                {[
                  ['bg-primary-light', 'bg-primary', 'bg-primary-dark', 'text-primary-contrast'],
                  [
                    'bg-secondary-light',
                    'bg-secondary',
                    'bg-secondary-dark',
                    'text-secondary-contrast',
                  ],
                  ['bg-accent-light', 'bg-accent', 'bg-accent-dark', 'text-accent-contrast'],
                  // ["bg-neutral-light", "bg-neutral-main", "bg-neutral-dark", "text-neutral-contrast"],
                  ['bg-success-light', 'bg-success', 'bg-success-dark', 'text-success-contrast'],
                  ['bg-warning-light', 'bg-warning', 'bg-warning-dark', 'text-warning-contrast'],
                  ['bg-error-light', 'bg-error', 'bg-error-dark', 'text-error-contrast'],
                  ['bg-info-light', 'bg-info', 'bg-info-dark', 'text-info-contrast'],
                ].map((colors) => {
                  const colorTitle = colors[0].split('-')[1];
                  return (
                    <GooeyVariantSection
                      key={colorTitle}
                      title={colorTitle}
                      direction='row'
                      variants={colors.map((color) => {
                        const title = color.split('-')[2];
                        const contrast = color.includes('contrast');
                        return {
                          title: title || 'main',
                          variant: contrast ? (
                            <div
                              key={color}
                              className={`${colors[1]} ${color} w-12 h-12 flex flex-col text-lg font-bold items-center justify-center`}
                            >
                              JG
                            </div>
                          ) : (
                            <div key={color} className={`${color} w-12 h-12`} />
                          ),
                        };
                      })}
                    />
                  );
                })}
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Typography'>
                <GooeyVariantSectionTitle title='Variants' className='mb-4' />
                {(
                  [
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'subtitle1',
                    'subtitle2',
                    'body1',
                    'body2',
                    'caption',
                  ] as const
                ).map((variant) => (
                  <Stack key={variant} spacing={1} className='mb-4'>
                    <div className='badge badge-neutral mb-2'>{variant}</div>
                    <Typography variant={variant} align='left' gutterBottom>
                      Mary had a little lamb
                    </Typography>
                  </Stack>
                ))}
                <GooeyVariantSectionTitle title='Colors' className='mb-4 mt-8' />
                {(
                  [
                    'primary',
                    'secondary',
                    'accent',
                    'success',
                    'error',
                    'warning',
                    'info',
                    'contrast',
                  ] as const
                ).map((color) => (
                  <Typography
                    key={color}
                    variant='h6'
                    color={color === 'contrast' ? 'primary' : color}
                    align='left'
                    contrast={color === 'contrast'}
                    gutterBottom
                  >
                    {color}
                  </Typography>
                ))}
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='IconButton'>
                <GooeyVariantSection
                  title='Shapes'
                  variants={(['rounded', 'circle', 'square'] as const).map((shape) => ({
                    title: shape,
                    variant: (
                      <IconButton key={shape} variant='contained' shape={shape} color='primary'>
                        <TablerIconMoodSmileFilled />
                      </IconButton>
                    ),
                  }))}
                />
                <GooeyVariantSection
                  title='Variants'
                  variants={(['contained', 'text', 'outlined'] as const).map((variant) => ({
                    title: variant,
                    variant: (
                      <IconButton key={variant} variant={variant} color='primary'>
                        <TablerIconMoodSmileFilled />
                      </IconButton>
                    ),
                  }))}
                />
                <GooeyVariantSection
                  title='Colors'
                  variants={(
                    [
                      'primary',
                      'secondary',
                      'accent',
                      'success',
                      'error',
                      'warning',
                      'info',
                    ] as const
                  ).map((color) => ({
                    title: color,
                    variant: (
                      <IconButton key={color} color={color} variant='contained'>
                        <TablerIconMoodSmileFilled />
                      </IconButton>
                    ),
                  }))}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Buttons'>
                <GooeyVariantSection
                  title='Colors'
                  variants={(
                    [
                      'primary',
                      'secondary',
                      'accent',
                      'success',
                      'error',
                      'warning',
                      'info',
                    ] as const
                  ).map((color) => ({
                    title: color,
                    variant: (
                      <Button key={color} variant='contained' color={color}>
                        button
                      </Button>
                    ),
                  }))}
                />
                <GooeyVariantSection
                  title='Sizes'
                  variants={(['small', 'medium', 'large', 'xlarge'] as const).map((size) => ({
                    title: size,
                    variant: (
                      <Button key={size} variant='contained' size={size}>
                        button
                      </Button>
                    ),
                  }))}
                />
                <GooeyVariantSection
                  title='Variants'
                  variants={(['contained', 'outlined', 'text'] as const).map((variant) => ({
                    title: variant,
                    variant: (
                      <Button key={variant} variant={variant}>
                        button
                      </Button>
                    ),
                  }))}
                />
                <GooeyVariantSection
                  title='Icons'
                  variants={(
                    ['disabled', 'loading-center', 'loading-start', 'loading-end'] as const
                  ).map((state) => ({
                    title: state,
                    variant: (
                      <Button
                        key={state}
                        disabled={state === 'disabled'}
                        loading={state.includes('loading')}
                        loadingPosition={
                          state.includes('loading')
                            ? // biome-ignore lint/suspicious/noExplicitAny: styleguide
                              (state.replace('loading-', '') as any)
                            : undefined
                        }
                      >
                        button
                      </Button>
                    ),
                  }))}
                />
                <GooeyVariantSection
                  title='States'
                  variants={(['disabled', 'loading-center'] as const).map((state) => ({
                    title: state,
                    variant: (
                      <Button
                        key={state}
                        disabled={state === 'disabled'}
                        loading={state.includes('loading')}
                        loadingPosition={
                          state.includes('loading')
                            ? // biome-ignore lint/suspicious/noExplicitAny: styleguide
                              (state.replace('loading-', '') as any)
                            : undefined
                        }
                      >
                        button
                      </Button>
                    ),
                  }))}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Alerts'>
                <GooeyVariantSection
                  title='Standard'
                  variants={(['error', 'info', 'warning', 'success', 'neutral'] as const).map(
                    (severity) => ({
                      title: severity,
                      variant: (
                        <Alert severity={severity} variant='standard' key={severity}>
                          This is an alert message.
                        </Alert>
                      ),
                    })
                  )}
                />
                <GooeyVariantSection
                  title='Filled'
                  variants={(['error', 'info', 'warning', 'success', 'neutral'] as const).map(
                    (severity) => ({
                      title: severity,
                      variant: (
                        <Alert severity={severity} variant='filled' key={severity}>
                          This is an alert message.
                        </Alert>
                      ),
                    })
                  )}
                />
                <GooeyVariantSection
                  title='Outlined'
                  variants={(['error', 'info', 'warning', 'success', 'neutral'] as const).map(
                    (severity) => ({
                      title: severity,
                      variant: (
                        <Alert
                          severity={severity}
                          key={severity}
                          variant='outlined'
                          onClose={() => {}}
                        >
                          This is an alert message.
                        </Alert>
                      ),
                    })
                  )}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Cards'>
                <GooeyVariantSection
                  title='Variants'
                  className='bg-transparent'
                  variants={[
                    {
                      title: 'basic',
                      variant: (
                        <Card variant='border'>
                          <CardHeader
                            title='Example title'
                            subtitle='Example subtitle'
                            action={<TablerIconX />}
                          />
                          <CardContent>
                            <Typography variant='body1'>
                              This is a card with some content.
                            </Typography>
                          </CardContent>
                          <CardActions>
                            <Button size='small'>Action A</Button>
                            <Button size='small' variant='outlined'>
                              Action B
                            </Button>
                          </CardActions>
                        </Card>
                      ),
                    },
                    {
                      title: 'avatar',
                      variant: (
                        <Card variant='border'>
                          <CardHeader
                            title='Example title'
                            subtitle='Example subtitle'
                            avatar={<Avatar src='https://github.com/zrosenbauer.png' />}
                          />
                          <CardContent>
                            <Typography variant='body1'>
                              This is a card with some content.
                            </Typography>
                          </CardContent>
                        </Card>
                      ),
                    },
                    {
                      title: 'flat',
                      variant: (
                        <Card variant='flat'>
                          <CardContent>
                            <Typography variant='h6'>Inline Title</Typography>
                            <Typography variant='body1'>
                              This is a card with some content.
                            </Typography>
                          </CardContent>
                        </Card>
                      ),
                    },
                    {
                      title: 'no-border',
                      variant: (
                        <Card variant='border' className='border-0'>
                          <CardContent>
                            <Typography variant='body1'>
                              This is a card with some content.
                            </Typography>
                          </CardContent>
                        </Card>
                      ),
                    },
                    {
                      title: 'interactive',
                      variant: (
                        <Card variant='border' interactive background='black'>
                          <CardContent>
                            <Typography variant='body1'>
                              This is a card with some content.
                            </Typography>
                          </CardContent>
                        </Card>
                      ),
                    },
                    {
                      title: 'disabled',
                      variant: (
                        <Card variant='border' disabled>
                          <CardContent>
                            <Typography variant='body1'>
                              This is a card with some content.
                            </Typography>
                          </CardContent>
                        </Card>
                      ),
                    },
                  ]}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Accordion'>
                <GooeyVariantSection
                  title='Variants'
                  variants={(['flat', 'border'] as const).map((variant) => ({
                    title: variant,
                    variant: (
                      <Accordion variant={variant} key={variant} className='min-w-96'>
                        {[
                          { summary: 'Summary 1', details: 'Details 1' },
                          { summary: 'Summary 2', details: 'Details 2' },
                          { summary: 'Summary 3', details: 'Details 3' },
                        ].map((item, index) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: styleguide
                          <AccordionItem key={index}>
                            <AccordionSummary>{item.summary}</AccordionSummary>
                            <AccordionDetails>{item.details}</AccordionDetails>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ),
                  }))}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Progress'>
                <GooeyVariantSection
                  title='Colors'
                  direction='column'
                  variants={[
                    ...(
                      [
                        'primary',
                        'secondary',
                        'accent',
                        'success',
                        'error',
                        'warning',
                        'info',
                      ] as const
                    ).map((color) => ({
                      title: color,
                      variant: <Progress className='w-full' key={color} color={color} value={45} />,
                    })),
                  ]}
                />
                <GooeyVariantSection
                  title='Sizes'
                  direction='column'
                  variants={[
                    ...(['xs', 'sm', 'md', 'lg'] as const).map((size) => ({
                      title: size,
                      variant: <Progress className='w-full' key={size} size={size} value={45} />,
                    })),
                  ]}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GooeySection title='Avatar'>
                <GooeyVariantSection
                  title='primary'
                  variants={[
                    {
                      title: 'primary',
                      variant: <Avatar src='https://github.com/zrosenbauer.png' />,
                    },
                    {
                      title: 'secondary',
                      variant: (
                        <Avatar>
                          <TablerIconMoodSmileFilled />
                        </Avatar>
                      ),
                    },
                  ]}
                />
                <GooeyVariantSection
                  title='variants'
                  variants={[
                    { title: 'circular', variant: <Avatar alt='avatar' /> },
                    { title: 'rounded', variant: <Avatar alt='avatar' variant='rounded' /> },
                    { title: 'square', variant: <Avatar alt='avatar' variant='square' /> },
                  ]}
                />
                <GooeyVariantSection
                  title='size'
                  variants={(['small', 'medium', 'large'] as const).map((size) => ({
                    title: size,
                    variant: <Avatar key={size} size={size} alt='avatar' />,
                  }))}
                />
              </GooeySection>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <GooeySection title='Dialogs'>
                <GooeyVariantSection
                  title='Sizes'
                  variants={[
                    {
                      title: 'full screen',
                      variant: (
                        <React.Fragment>
                          <Dialog trigger={<DialogTrigger>Open</DialogTrigger>} fullScreen>
                            <DialogTitle closeable>Dialog Title</DialogTitle>
                            <DialogContent>Example content.</DialogContent>
                            <DialogActions>
                              <Button>Confirm</Button>
                              <DialogClose variant='outlined'>Close</DialogClose>
                            </DialogActions>
                          </Dialog>
                        </React.Fragment>
                      ),
                    },
                    ...(['xs', 'sm', 'md', 'lg', 'xl', '2xl', 'full'] as const).map((size) => ({
                      title: size,
                      variant: (
                        <Dialog
                          trigger={<DialogTrigger>Open</DialogTrigger>}
                          key={size}
                          maxWidth={size}
                        >
                          <DialogTitle closeable>Dialog Title</DialogTitle>
                          <DialogContent>Example content.</DialogContent>
                          <DialogActions>
                            <Button>Confirm</Button>
                            <DialogClose variant='outlined'>Close</DialogClose>
                          </DialogActions>
                        </Dialog>
                      ),
                    })),
                  ]}
                />
              </GooeySection>
            </Grid>
            <Grid size={12}>
              <GooeySection title='Grid'>
                {(
                  [
                    {
                      items: [
                        { title: '1', size: 6 },
                        { title: '2', size: { xs: 12, md: 6 } },
                        { title: '3', size: 12 },
                      ],
                      columns: 12,
                      spacing: { xs: 2, sm: 4, md: 8 },
                      rowSpacing: { xs: 8, sm: 4, md: 2 },
                    },
                  ] as const
                ).map(({ items, spacing, rowSpacing, columns }) => (
                  <Grid
                    container
                    columns={columns}
                    spacing={spacing}
                    rowSpacing={rowSpacing}
                    key={items.join('-')}
                    className='mb-4'
                  >
                    {items.map((item) => (
                      <Grid key={JSON.stringify(item)} size={item.size}>
                        <div className='bg-gray-800 p-4'>{item.title}</div>
                      </Grid>
                    ))}
                  </Grid>
                ))}
              </GooeySection>
            </Grid>
            <Grid size={12}>
              <GooeySection title='Stack'>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'start', lg: 'center' }}
                  className='mb-8'
                >
                  <div className='bg-blue-700 p-4 h-32'>Item 1</div>
                  <div className='bg-blue-800 p-4 h-24'>Item 2</div>
                  <div className='bg-blue-900 p-4 h-48'>Item 3</div>
                </Stack>

                <Stack
                  direction='column'
                  spacing={{ xs: 2, lg: 8 }}
                  alignItems='center'
                  className='mb-8'
                >
                  <div className='bg-green-700 p-4 w-32'>Centered</div>
                  <div className='bg-green-800 p-4 w-48'>Items</div>
                  <div className='bg-green-900 p-4 w-24'>Stack</div>
                </Stack>

                <Stack spacing={2} divider={<hr className='border-gray-300' />}>
                  <div className='bg-purple-700 p-4'>Divided</div>
                  <div className='bg-purple-800 p-4'>Stack</div>
                  <div className='bg-purple-900 p-4'>Items</div>
                </Stack>
              </GooeySection>
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
}

/*
|------------------
| Internals
|------------------
*/

interface GooeySectionProps extends React.PropsWithChildren<CommonProps<HTMLDivElement>> {
  title: string;
  /** @default false */
  defaultClosed?: boolean;
}

function GooeySection({
  title,
  children,
  className,
  defaultClosed = false,
  ref,
  ...props
}: GooeySectionProps) {
  const [open, setOpen] = React.useState(!defaultClosed);
  return (
    <div
      {...props}
      ref={ref}
      id={_.kebabCase(title)}
      className={twMerge(
        'styleguide-section flex flex-col items-start justify-stretch w-full rounded-xl border border-gray-700',
        className
      )}
    >
      <div className='flex flex-row items-center justify-between w-full relative p-4'>
        <h2 className='text-3xl text-left w-full'>{title}</h2>
        <IconButton
          variant='text'
          color='primary'
          onClick={() => {
            setOpen(!open);
          }}
          className='ml-4 absolute top-3 right-3'
        >
          <TablerIconChevronDown
            className={twMerge(open ? 'rotate-180 transition-all' : 'rotate-0 transition-all')}
          />
        </IconButton>
      </div>
      <div className={twMerge('w-full p-4 border-t border-gray-700', open ? 'block' : 'hidden')}>
        {children}
      </div>
    </div>
  );
}

interface GooeyVariantSectionTitleProps extends CommonProps<HTMLHeadingElement> {
  title: string;
}

function GooeyVariantSectionTitle({ title, className, ...props }: GooeyVariantSectionTitleProps) {
  return (
    <h3 className={twMerge('text-3xl w-full', className)} {...props}>
      {title}
    </h3>
  );
}

interface GooeyVariantSectionProps extends CommonProps {
  title: string;
  variants: Array<{ title: string; variant: React.ReactNode }>;
  direction?: 'row' | 'column';
  className?: string;
}

function GooeyVariantSection({
  title,
  variants,
  direction = 'row',
  className,
}: GooeyVariantSectionProps) {
  return (
    <div
      className={twMerge(
        'gooey-variants-section',
        'w-full flex flex-col gap-y-8 mb-8 py-8 px-4 bg-background-paper rounded-xl',
        className
      )}
    >
      <GooeyVariantSectionTitle title={title} />
      <div
        className={twMerge(
          'flex w-full',
          direction === 'row' && 'flex-row gap-x-2 gap-y-4 items-start flex-wrap justify-start',
          direction === 'column' && 'flex-col gap-x-4 items-start justify-start'
        )}
      >
        {variants.map((item) => (
          <div
            key={item.title}
            className={twMerge('flex flex-col', direction === 'column' && 'w-full')}
          >
            {item.variant}
            <p
              className={twMerge(
                'text-md block mt-1',
                direction === 'row' && 'text-left',
                direction === 'column' && 'text-center mx-auto'
              )}
            >
              {item.title}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
